import React, { useState } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { connect } from 'react-redux'
import { useBidirectionalPresence } from '@adc/presence'
import { DataTableWrapper, PageLayout, PresenceModal } from '@adc/ui-morpheus'
import { ROOT_CRUMB } from '../../constants/routing'
import { VENUES } from '../../constants/tableTypes'
import {
  fetchTable,
  fetchTableAppend,
  fetchTableBatches,
  abortTableValues,
  resetTableValues,
  updateSuggestions,
  addFilter,
  clearFilters,
  removeFilter,
  changeSortBy,
  savedSearchActions,
} from '../../actions/venues'
import { apiToUiDataRowsTranslation } from '../../translations/venues'
import {
  columnSelector,
  getFilterClauses,
} from '../../reducers/venues/columnDefinitions'
import venuesBulkUpdateModule from '../../components/venuesBulkUpdateModule'
import { mapDispatchToSavedSearchActionsWithLimit } from '../../util/savedSearchLimitCheck'

const TITLE = 'Venues'

const VenuesPage = props => {
  const tableProps = {
    columns: props.columns,
    filterClauses: props.filterClauses,
    addFilter: props.addFilter,
    removeFilter: props.removeFilter,
    clearFilters: props.clearFilters,
    changeSortBy: props.changeSortBy,
    fetchDataRows: props.fetchDataRows,
    fetchDataRowsAppend: props.fetchDataRowsAppend,
    isLoading: props.isLoading,
    errorInfo: props.errorInfo,
    rowData: props.rowData,
    updateSuggestions: props.updateSuggestions,
    suggestions: props.suggestions,
    totalCount: props.totalCount,
    downloadData: props.downloadData,
    downloadActions: props.downloadActions,
    savedSearchesActions: props.savedSearchesActions,
    savedSearches: props.savedSearches,
    userAuthorities: props.userAuthorities,
  }

  const [context] = useState({ action: 'viewing' })
  const presence = useBidirectionalPresence('morpheus.venues.all', context)

  const buttons = [<PresenceModal key='presence' {...presence} />]

  const bulkUpdateProps = {
    entityName: TITLE,
    bulkProcessingModules: [venuesBulkUpdateModule],
  }

  return (
    <PageLayout title={TITLE} crumbs={[ROOT_CRUMB, TITLE]} buttons={buttons}>
      <DataTableWrapper
        {...props.config}
        {...tableProps}
        {...bulkUpdateProps}
      />
    </PageLayout>
  )
}

VenuesPage.propTypes = {
  config: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  filterClauses: PropTypes.array.isRequired,
  addFilter: PropTypes.func.isRequired,
  removeFilter: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
  changeSortBy: PropTypes.func.isRequired,
  fetchDataRows: PropTypes.func.isRequired,
  fetchDataRowsAppend: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  errorInfo: PropTypes.object,
  rowData: PropTypes.array.isRequired,
  updateSuggestions: PropTypes.func.isRequired,
  suggestions: PropTypes.object.isRequired,
  totalCount: PropTypes.number,
  downloadData: PropTypes.object.isRequired,
  downloadActions: PropTypes.shape({
    fetchData: PropTypes.func.isRequired,
    abort: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
  }),
  savedSearches: PropTypes.object,
  savedSearchesActions: PropTypes.object,
  userAuthorities: PropTypes.array,
}

const getTotalCount = state => state.total_count

const mapStateToProps = store => {
  const { content, isLoading, errorInfo, venuesUpdated, venueCount } =
    store[VENUES].data
  const downloadDataRaw = store[VENUES].downloadData
  const downloadContent = downloadDataRaw.content
  const downloadData = {
    content: downloadContent ? apiToUiDataRowsTranslation(downloadContent) : [],
    totalCount: downloadDataRaw.total_count,
    totalRetrieved: downloadDataRaw.total_elements,
    errorInfo: downloadDataRaw.errorInfo,
    isBatchSuccessful: downloadDataRaw.isBatchSuccessful,
  }
  const config = store[VENUES].tableInfo.config

  return {
    config,
    isLoading: !!isLoading,
    columns: columnSelector(store[VENUES].tableInfo),
    filterClauses: getFilterClauses(store[VENUES].tableInfo),
    rowData: apiToUiDataRowsTranslation(content),
    totalCount: getTotalCount(store[VENUES].data),
    suggestions: store[VENUES].suggestions,
    errorInfo,
    downloadData,
    venuesUpdated,
    venueCount,
    savedSearches: store[VENUES].savedSearches,
    userAuthorities: _.get(store, 'user.authorities', []),
  }
}

const mapDispatchToProps = dispatch => ({
  fetchDataRows: () => dispatch(fetchTable()),
  fetchDataRowsAppend: () => dispatch(fetchTableAppend()),
  addFilter: filterClause => dispatch(addFilter(filterClause)),
  removeFilter: filterClause => dispatch(removeFilter(filterClause)),
  clearFilters: _ => dispatch(clearFilters()),
  changeSortBy: sortValues => dispatch(changeSortBy(sortValues)),
  updateSuggestions: fetchOptions => dispatch(updateSuggestions(fetchOptions)),
  downloadActions: {
    fetchData: () => dispatch(fetchTableBatches()),
    abort: () => dispatch(abortTableValues(VENUES, true)),
    reset: () => dispatch(resetTableValues(VENUES, true)),
  },
  savedSearchesActions: mapDispatchToSavedSearchActionsWithLimit(
    dispatch,
    savedSearchActions,
    VENUES
  ),
})

export default connect(mapStateToProps, mapDispatchToProps)(VenuesPage)
